import React, { useState } from 'react';
import { Form, Select, Radio, Badge, Button, useToast, Alert, AlertDescription } from '@crisellevaldez/speculo-ui';
import { PlusCircle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { TagInput } from '../../common/TagInput';
import { TipBox } from '../onboarding/components/TipBox';
import { useOrganizationUsers } from '../../../hooks/useOrganizationUsers';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { AddTeamMemberModal } from './AddTeamMemberModal';

// Shared Integration Setup types - exported for reuse
export interface IntegrationSetupData {
  contactType: 'buyer' | 'seller' | 'both' | '';
  crmSourceType: 'random' | 'specific' | '';
  crmSources: string[];
  useTags: boolean;
  tagsAction: 'include' | 'exclude' | '';
  tags: string[];
  useAssignment: boolean;
  assignmentAction: 'include' | 'exclude' | '';
  assignedAgents: string[];
  additionalNotes: string;
}

interface IntegrationSetupStepProps {
  integrationSetup: IntegrationSetupData;
  onIntegrationSetupChange: (data: IntegrationSetupData) => void;
  flowName?: string;
}

/**
 * Reusable Integration Setup Step component
 * Used in both ConvertFlowSteps and RequestIntegrationModal
 */
export const IntegrationSetupStep: React.FC<IntegrationSetupStepProps> = ({
  integrationSetup,
  onIntegrationSetupChange,
  flowName,
}) => {
  const { users, loading: loadingUsers, addTeamMember } = useOrganizationUsers();
  const { organization } = useOrganizationStore();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [showAddTeamMember, setShowAddTeamMember] = useState(false);
  const [isAddingTeamMember, setIsAddingTeamMember] = useState(false);


  return (
    <div className="space-y-6">
      <Alert variant="info" className="mb-6">
        <AlertDescription>
          <span className="font-semibold">Integration Setup Required</span>
          <br />
          Your CRM needs to be integrated with {flowName ? `"${flowName}" convert` : 'this'} flow by
          the Speculo.ai team. Please provide the following information:
        </AlertDescription>
      </Alert>

      <Form.Control>
        <Form.Label>
          Contact Type <span className="text-red-500">*</span>
        </Form.Label>
        <Select
          options={[
            { value: 'buyer', label: 'Buyer' },
            { value: 'seller', label: 'Seller' },
            { value: 'both', label: 'Both' },
          ]}
          value={integrationSetup.contactType}
          onChange={(value) =>
            onIntegrationSetupChange({
              ...integrationSetup,
              contactType: value as 'buyer' | 'seller' | 'both',
            })
          }
          placeholder="Select contact type"
        />
      </Form.Control>

      <div className="border border-gray-200 dark:border-white/10 rounded-lg p-4 space-y-4">
        <Form.Control>
          <Form.Label>
            CRM Source Type <span className="text-red-500">*</span>
          </Form.Label>
          <div className="flex gap-4 mt-2">
            <Radio
              id="crmSourceType-random"
              value="random"
              checked={integrationSetup.crmSourceType === 'random'}
              onChange={() =>
                onIntegrationSetupChange({
                  ...integrationSetup,
                  crmSourceType: 'random',
                })
              }
              label="Random (Any source)"
            />
            <Radio
              id="crmSourceType-specific"
              value="specific"
              checked={integrationSetup.crmSourceType === 'specific'}
              onChange={() =>
                onIntegrationSetupChange({
                  ...integrationSetup,
                  crmSourceType: 'specific',
                })
              }
              label="Specific source(s)"
            />
          </div>
        </Form.Control>

        {integrationSetup.crmSourceType === 'specific' && (
          <div className="pl-4 border-l-2 border-blue-200 dark:border-blue-700/50 space-y-4">
            <Form.Control>
              <Form.Label>
                CRM Source(s) <span className="text-red-500">*</span>
              </Form.Label>
              <TagInput
                tags={integrationSetup.crmSources || []}
                onTagsChange={(newTags) =>
                  onIntegrationSetupChange({
                    ...integrationSetup,
                    crmSources: newTags,
                  })
                }
                placeholder="Type source name and press Enter"
                showTagIcon={false}
              />
              <Form.HelperText className="text-muted-foreground mt-1">
                Will be used to filter contacts. Please list the exact name(s) of the source(s) in
                your CRM that you want connected to this flow.{' '}
                <span className="font-medium text-red-600">
                  CRITICAL: We require an exact match for the integration. Please copy/paste the
                  name(s) directly from your CRM.
                </span>
              </Form.HelperText>
            </Form.Control>
          </div>
        )}
      </div>

      <div className="border border-gray-200 dark:border-white/10 rounded-lg p-4 space-y-4">
        <Form.Control>
          <Form.Label>Should any tags be considered to filter out specific contacts?</Form.Label>
          <div className="flex gap-4 mt-2">
            <Radio
              id="useTags-yes"
              value="yes"
              checked={integrationSetup.useTags === true}
              onChange={() =>
                onIntegrationSetupChange({
                  ...integrationSetup,
                  useTags: true,
                })
              }
              label="Yes"
            />
            <Radio
              id="useTags-no"
              value="no"
              checked={integrationSetup.useTags === false}
              onChange={() =>
                onIntegrationSetupChange({
                  ...integrationSetup,
                  useTags: false,
                  tagsAction: '',
                  tags: [],
                })
              }
              label="No"
            />
          </div>
        </Form.Control>

        {integrationSetup.useTags && (
          <div className="pl-4 border-l-2 border-blue-200 dark:border-blue-700/50 space-y-4">
            <Form.Control>
              <Form.Label>Which tags should we include or exclude from sending to Remi?</Form.Label>
              <div className="flex gap-4 mt-2">
                <Radio
                  id="tagsAction-include"
                  value="include"
                  checked={integrationSetup.tagsAction === 'include'}
                  onChange={() =>
                    onIntegrationSetupChange({
                      ...integrationSetup,
                      tagsAction: 'include',
                    })
                  }
                  label="Include"
                />
                <Radio
                  id="tagsAction-exclude"
                  value="exclude"
                  checked={integrationSetup.tagsAction === 'exclude'}
                  onChange={() =>
                    onIntegrationSetupChange({
                      ...integrationSetup,
                      tagsAction: 'exclude',
                    })
                  }
                  label="Exclude"
                />
              </div>
            </Form.Control>
            <Form.Control>
              <Form.Label>Tags</Form.Label>
              <TagInput
                tags={integrationSetup.tags || []}
                onTagsChange={(newTags) =>
                  onIntegrationSetupChange({
                    ...integrationSetup,
                    tags: newTags,
                  })
                }
                placeholder="Type tag name and press Enter"
              />
              <TipBox>These tags will be used to filter contacts for this flow.</TipBox>
            </Form.Control>
          </div>
        )}
      </div>

      <div className="border border-gray-200 dark:border-white/10 rounded-lg p-4 space-y-4">
        <Form.Control>
          <Form.Label>
            Should agent assignment be considered to filter out specific contacts?
          </Form.Label>
          <div className="flex gap-4 mt-2">
            <Radio
              id="useAssignment-yes"
              value="yes"
              checked={integrationSetup.useAssignment === true}
              onChange={() =>
                onIntegrationSetupChange({
                  ...integrationSetup,
                  useAssignment: true,
                })
              }
              label="Yes"
            />
            <Radio
              id="useAssignment-no"
              value="no"
              checked={integrationSetup.useAssignment === false}
              onChange={() =>
                onIntegrationSetupChange({
                  ...integrationSetup,
                  useAssignment: false,
                  assignmentAction: '',
                  assignedAgents: [],
                })
              }
              label="No"
            />
          </div>
        </Form.Control>

        {integrationSetup.useAssignment && (
          <div className="pl-4 border-l-2 border-blue-200 dark:border-blue-700/50 space-y-4">
            <Form.Control>
              <Form.Label>
                Which assigned agents should be included/excluded to send to Remi?
              </Form.Label>
              <div className="flex gap-4 mt-2">
                <Radio
                  id="assignmentAction-include"
                  value="include"
                  checked={integrationSetup.assignmentAction === 'include'}
                  onChange={() =>
                    onIntegrationSetupChange({
                      ...integrationSetup,
                      assignmentAction: 'include',
                    })
                  }
                  label="Include"
                />
                <Radio
                  id="assignmentAction-exclude"
                  value="exclude"
                  checked={integrationSetup.assignmentAction === 'exclude'}
                  onChange={() =>
                    onIntegrationSetupChange({
                      ...integrationSetup,
                      assignmentAction: 'exclude',
                    })
                  }
                  label="Exclude"
                />
              </div>
            </Form.Control>
            <Form.Control>
              <Form.Label>Agent Name(s):</Form.Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Select
                    isLoading={loadingUsers}
                    options={users}
                    rows={4}
                    value={integrationSetup.assignedAgents || []}
                    onChange={(value) => {
                      if (Array.isArray(value)) {
                        onIntegrationSetupChange({
                          ...integrationSetup,
                          assignedAgents: value,
                        });
                      }
                    }}
                    placeholder="Select team members"
                    multiple={true}
                    searchable
                  />
                </div>
                <Button
                  variant="secondary"
                  className="whitespace-nowrap h-9"
                  onClick={() => setShowAddTeamMember(true)}
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Add New Team
                </Button>
              </div>
            </Form.Control>

            <AddTeamMemberModal
              isOpen={showAddTeamMember}
              onClose={() => setShowAddTeamMember(false)}
              onSubmit={async (data) => {
                setIsAddingTeamMember(true);
                try {
                  if (!organization?.id) throw new Error('No organization ID');
                  await addTeamMember(organization.id, data);
                  await queryClient.invalidateQueries({ queryKey: ['organizationUsers'] });
                  setShowAddTeamMember(false);
                  showToast({ message: 'Team member invited successfully', variant: 'success' });
                } catch (error) {
                  showToast({
                    message: error instanceof Error ? error.message : 'Error inviting team member',
                    variant: 'error',
                  });
                } finally {
                  setIsAddingTeamMember(false);
                }
              }}
              isSubmitting={isAddingTeamMember}
            />
          </div>
        )}
      </div>

      <Form.Control>
        <Form.Label>Any other notes we should know about this flow?</Form.Label>
        <textarea
          className="shadow-sm w-full border rounded-md border-gray-300 dark:border-white/20 py-2 px-3 text-foreground text-sm placeholder-gray-500 dark:placeholder-gray-400 placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-gray-400 dark:focus:ring-white/20 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-white/5 disabled:text-gray-500 dark:disabled:text-gray-400 bg-white dark:bg-white/5 min-h-[100px] resize-y"
          placeholder="Enter any additional notes or special requirements for this integration..."
          value={integrationSetup.additionalNotes || ''}
          onChange={(e) =>
            onIntegrationSetupChange({
              ...integrationSetup,
              additionalNotes: e.target.value,
            })
          }
        />
      </Form.Control>
    </div>
  );
};

/**
 * Reusable Integration Setup Summary component
 * Used in flow summary step to display integration setup info
 */
interface IntegrationSetupSummaryProps {
  integrationSetup: IntegrationSetupData;
}

export const IntegrationSetupSummary: React.FC<IntegrationSetupSummaryProps> = ({
  integrationSetup,
}) => {
  const { users } = useOrganizationUsers();

  if (!integrationSetup || !integrationSetup.contactType) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <div className="text-sm font-medium text-foreground">Contact Type</div>
          <div className="text-sm mt-2 text-muted-foreground capitalize">
            {integrationSetup.contactType || 'Not specified'}
          </div>
        </div>
        <div>
          <div className="text-sm font-medium text-foreground">CRM Source Type</div>
          <div className="text-sm mt-2 text-muted-foreground">
            {integrationSetup.crmSourceType === 'random'
              ? 'Random (Any source)'
              : integrationSetup.crmSourceType === 'specific'
                ? 'Specific source(s)'
                : 'Not specified'}
          </div>
        </div>
      </div>
      {integrationSetup.crmSources?.length > 0 && (
        <div>
          <div className="text-sm font-medium text-foreground">CRM Source(s)</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {integrationSetup.crmSources.map((source: string, index: number) => (
              <Badge key={index} variant="info">
                {source}
              </Badge>
            ))}
          </div>
        </div>
      )}
      {integrationSetup.useTags && integrationSetup.tags?.length > 0 && (
        <div>
          <div className="text-sm font-medium text-foreground">
            Tags ({integrationSetup.tagsAction === 'include' ? 'Include' : 'Exclude'})
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {integrationSetup.tags.map((tag: string, index: number) => (
              <Badge key={index} variant="warning">
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      )}
      {integrationSetup.useAssignment && integrationSetup.assignedAgents?.length > 0 && (
        <div>
          <div className="text-sm font-medium text-foreground">
            Agent Assignment (
            {integrationSetup.assignmentAction === 'include' ? 'Include' : 'Exclude'})
          </div>
          <div className="text-sm mt-2 text-muted-foreground">
            {users
              .filter((u) => integrationSetup.assignedAgents?.includes(u.value))
              .map((u) => `${u.firstName} ${u.lastName}`)
              .join(', ') || 'None selected'}
          </div>
        </div>
      )}
      {integrationSetup.additionalNotes && (
        <div>
          <div className="text-sm font-medium text-foreground">Additional Notes</div>
          <div className="text-sm mt-2 text-muted-foreground whitespace-pre-wrap">
            {integrationSetup.additionalNotes}
          </div>
        </div>
      )}
    </div>
  );
};
